import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { Fade } from '@mui/material';
import { Suspense, useMemo } from 'react';
import { AppHeader } from '@/components/Header/AppHeader';
import { Sidebar } from '@/components/Sidebar/Sidebar';
import { useJmap } from '@/contexts/JmapContext';
import { PageSkeleton } from '@/components/PageSkeleton';

/**
 * Persistent app shell. Mounted once for the lifetime of the authenticated
 * session; route changes only swap what's rendered inside <Outlet />, so the
 * AppHeader and module-switcher rail never unmount or refetch their data.
 *
 * The page-content area is wrapped in a keyed <Fade> so route changes get a
 * soft transition without touching the chrome.
 *
 * The keyboard-shortcuts menu item lives inside <AppHeader>; it dispatches a
 * `webmail:show-shortcuts` window event so the Mailbox page's local dialog
 * state can react without prop-drilling through the layout.
 */
export function MainLayout() {
  const onShowKeyboardShortcuts = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('webmail:show-shortcuts'));
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { token, accountId } = useJmap();
  const location = useLocation();

  // Key the fade on the first path segment so deep navigations within a
  // single page (e.g. picking different mailboxes inside /mail) don't keep
  // re-fading mid-interaction. Sub-route navigation feels instant.
  const transitionKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments[0] || '/';
  }, [location.pathname]);

  return (
    <>
      <AppHeader onShowKeyboardShortcuts={onShowKeyboardShortcuts} />
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 64px)',
          mt: '64px',
          position: 'relative',
        }}
      >
        {/* Module-switcher rail — always rendered so it never re-mounts on
            route changes. Hidden on mobile (we use a different nav pattern). */}
        {!isMobile && token && accountId && (
          <Sidebar token={token} accountId={accountId} miniOnly />
        )}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Fade key={transitionKey} in appear timeout={180}>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Suspense lives INSIDE the layout shell so a lazy-loaded
                  route chunk only swaps the content area — the AppHeader
                  and module rail stay mounted. PageSkeleton picks the
                  right skeleton shape from the URL so users see the
                  page's outline while the chunk and its data resolve. */}
              <Suspense fallback={<PageSkeleton />}>
                <Outlet />
              </Suspense>
            </Box>
          </Fade>
        </Box>
      </Box>
    </>
  );
}
